import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { CustomOnboardingRedirectionParams, Page } from "../../../activities/AppPage";
import { HRQuestion, HRQuestionGroupId, HRQuestionId, HRSubSectionId } from "../../../messages";
import { HRApplicationForm, HRQuestionGroup, HRSection } from "../../../messages/HR";
import { assertDefined } from "../../../utils/assertUndefinedable";
import { HashMap } from "../../../utils/HashMap";
import {
  getQuestionGroupTitle,
  getQuestionText,
  isAnswerableQuestion,
  isQuestionAnswered,
  stageHasRejectedDocumentComplianceQuestions,
  stageHasRejectedQuestionAnswers,
} from "../../Onboarding/utils/onboardingUtils";
import useNextStageModal from "./useNextStageModal";

interface Props {
  section: HRSection;
  sections: HRSection[];
  caregiverApplicationQuery: UseQueryResult<HRApplicationForm, unknown>;
  language: string;
  hasCompletedOrientation: boolean;
  customDirectionParams: CustomOnboardingRedirectionParams | null;
  onRequestNavigateReplace: (page: Page) => void;
  onRequestStartWatchingOrientation: () => void;
  onClickGoBack: () => void;
}

const useStage = (props: Props) => {
  const parentIdsAnswers = useRef<HashMap<HRQuestionId, number | null>>(HashMap.empty());
  const parentIdsMap = useRef<HashMap<HRQuestionId, HRQuestionId>>(HashMap.empty());
  const questionParentAnswerMap = useRef<HashMap<HRQuestionId, number>>(HashMap.empty());
  const questionsMap = useRef<HashMap<HRQuestionId, HRQuestion>>(HashMap.empty());
  const [isLoading, setIsLoading] = useState(false);
  const {
    questionGroupIdToSubsectionId,
    questionGroupIdShareUrlMap,
    questionGroupIdComplianceMap,
  } = flattenSectionGroups(props.section);

  const nextStageModal = useNextStageModal({
    section: props.section,
    sections: props.sections,
    language: props.language,
    hasCompletedOrientation: props.hasCompletedOrientation,
    caregiverApplicationQuery: props.caregiverApplicationQuery,
    onRequestNavigateReplace: props.onRequestNavigateReplace,
    onRequestStartWatchingOrientation: props.onRequestStartWatchingOrientation,
    onClickGoBack: props.onClickGoBack,
    onClickGoToFirstUnansweredStage: goToUnansweredStage,
  });

  const setParentAnswers = (group: HRQuestionGroup) => {
    for (const question of group.questions) {
      questionsMap.current = questionsMap.current.set(question.id, question);

      if (question.parentId !== null) {
        parentIdsMap.current = parentIdsMap.current.set(question.id, question.parentId);
      }

      if (question.parentAnswer !== null) {
        questionParentAnswerMap.current = questionParentAnswerMap.current.set(
          question.id,
          Number(question.parentAnswer)
        );
      }

      if ("chosenAnswerId" in question && question.chosenAnswerId !== null) {
        parentIdsAnswers.current = parentIdsAnswers.current.set(
          question.id,
          question.chosenAnswerId
        );
      }
    }
  };

  const [questionGroups, setQuestionGroups] = useState(() => {
    const { groups } = flattenSectionGroups(props.section);
    groups.forEach((group) => setParentAnswers(group));
    return groups;
  });

  const queryClient = useQueryClient();

  const initialIndex =
    props.customDirectionParams !== null
      ? getInitialStageIndexForDirections(
          questionGroups,
          questionGroupIdToSubsectionId,
          props.customDirectionParams
        )
      : getInitialStageIndex(questionGroups);

  const [currentIndex, setIndex] = useState(
    initialIndex >= 0 ? initialIndex : questionGroups.length - 1
  );
  const currentStage = questionGroups[currentIndex];

  const shouldQuestionBeHidden = (question: HRQuestion) => {
    const parentsToCheck =
      question.isComplianceFollowUp !== true
        ? getAllParents(question, parentIdsMap.current, questionsMap.current)
        : [];

    return [question, ...parentsToCheck].some((parent) =>
      shouldQuestionBeHiddenCheck({
        question: parent,
        parentIdsAnswers: parentIdsAnswers.current,
      })
    );
  };

  const displayStages = questionGroups.filter((group) =>
    group.questions.some((question) => !shouldQuestionBeHidden(question))
  );
  const totalStages = displayStages.length;
  const currentStageIndex = displayStages.findIndex(
    (stage) => stage.data.id === currentStage.data.id
  );
  const currentStageNumber = currentStageIndex + 1;
  const hasPreviousStage =
    displayStages.length > 0 && displayStages[0].data.id !== currentStage.data.id;
  const percentage = Math.ceil((currentStageNumber / totalStages) * 100);

  const getNextStageIndex = () =>
    questionGroups.findIndex(
      (group, i) =>
        i > currentIndex && group.questions.some((question) => !shouldQuestionBeHidden(question))
    );

  const hasNextStage = () => getNextStageIndex() !== -1;

  const getNextStageWithRejectedAnswersIndex = () => {
    if (!hasNextStage()) {
      return -1;
    }

    const nextStageWithRejectedAnswersIndex = questionGroups.findIndex(
      (x, i) => i > currentIndex && stageHasRejectedQuestionAnswers(x)
    );

    return nextStageWithRejectedAnswersIndex === -1
      ? getNextStageIndex()
      : nextStageWithRejectedAnswersIndex;
  };

  const goToNextStage = (newSections?: HRSection[]) => {
    const nextQuestionGroupIndex = getNextStageIndex();

    if (nextQuestionGroupIndex === -1) {
      return nextStageModal.handleSectionFinished(newSections);
    }

    setIndex(nextQuestionGroupIndex);
  };

  const goToPreviousStage = () => {
    if (!hasPreviousStage) {
      return;
    }

    let previousIndex = currentIndex;

    do {
      previousIndex = previousIndex - 1;
    } while (
      previousIndex > 0 &&
      !questionGroups[previousIndex].questions.some((question) => !shouldQuestionBeHidden(question))
    );

    setIndex(previousIndex);
  };

  function goToUnansweredStage() {
    goToStage(getInitialStageIndex(questionGroups));
  }

  const goToStage = (index: number) => {
    setIndex(index);
  };

  const handleAnswers = (group: HRQuestionGroup) => {
    const newGroup = getQuestionGroupWithIsHidden(group);

    setParentAnswers(newGroup);

    updateSectionsQuery(newGroup);

    updateQuestionGroup(newGroup);

    if (stageHasRejectedQuestionAnswers(group)) {
      setIsLoading(true);
      const nextStageWithRejectedAnswersIndex = getNextStageWithRejectedAnswersIndex();
      if (nextStageWithRejectedAnswersIndex !== -1) {
        setIndex(nextStageWithRejectedAnswersIndex);
        setIsLoading(false);
      } else {
        props.caregiverApplicationQuery
          .refetch()
          .then(() => props.onClickGoBack())
          .finally(() => setIsLoading(false));
      }
    } else if (!hasNextStage()) {
      setIsLoading(true);
      props.caregiverApplicationQuery
        .refetch()
        .then((result) => goToNextStage(result.data?.sections))
        .finally(() => setIsLoading(false));
    } else {
      goToNextStage();
    }
  };

  const updateQuestionGroup = (group: HRQuestionGroup) => {
    const index = questionGroups.findIndex((g) => g.data.id === group.data.id);

    setQuestionGroups([
      ...questionGroups.slice(0, index).map(getQuestionGroupWithIsHidden),
      {
        ...group,
        questions: group.questions.map(removeAnswerRejectionStatus),
      },
      ...questionGroups.slice(index + 1).map(getQuestionGroupWithIsHidden),
    ]);
  };

  const getQuestionGroupWithIsHidden = (group: HRQuestionGroup) => {
    return {
      ...group,
      questions: group.questions.map((question) => ({
        ...question,
        isHidden: shouldQuestionBeHidden(question),
      })),
    };
  };

  const updateSectionsQuery = (group: HRQuestionGroup) => {
    queryClient.setQueryData<HRApplicationForm>(["caregiver-application"], (old) => {
      if (old?.sections === undefined) {
        return {} as any;
      }

      return {
        ...old,
        sections: old.sections.map((s) =>
          s.data.id === props.section.data.id
            ? {
                data: s.data,
                subSections: s.subSections.map((ss) => ({
                  ...ss,
                  questionGroups: ss.questionGroups.map((qg) =>
                    qg.data.id === group.data.id
                      ? {
                          ...group,
                          questions: group.questions.map(removeAnswerRejectionStatus),
                        }
                      : {
                          data: qg.data,
                          questions: qg.questions.map((q) => ({
                            ...q,
                            isHidden: shouldQuestionBeHidden(q),
                          })),
                        }
                  ),
                })),
              }
            : s
        ),
      };
    });
  };

  const removeAnswerRejectionStatus = (question: HRQuestion): HRQuestion => {
    if (question.answerRejectionStatus.status === "Rejected") {
      return {
        ...question,
        answerRejectionStatus: {
          status: "Not Rejected",
        },
      };
    }
    return question;
  };

  return {
    state: {
      currentStage: currentStage,
      percentage: percentage,
      hasPreviousStage: hasPreviousStage,
      currentStageNumber: currentStageNumber,
      totalStages: totalStages,
      questionGroups: questionGroups,
      shareUrl: questionGroupIdShareUrlMap.get(currentStage.data.id) ?? null,
      isCompliant: questionGroupIdComplianceMap.get(currentStage.data.id) ?? null,
      isLoading: isLoading,
      title: extractTitleFromQuestionGroup(currentStage, props.language),
      questionGroupIdToSubsectionId: questionGroupIdToSubsectionId,
    },
    actions: {
      goToPreviousStage: goToPreviousStage,
      goToStage: goToStage,
      handleAnswers: handleAnswers,
    },
    nextStageModal,
  };
};

function getAllParents(
  question: HRQuestion,
  parentIdsMap: HashMap<HRQuestionId, HRQuestionId>,
  questionsMap: HashMap<HRQuestionId, HRQuestion>
) {
  const parents: HRQuestion[] = [];

  if (question.parentId === null) {
    return [];
  }

  let parentId = question.parentId;

  if (questionsMap.has(parentId)) {
    parents.push(assertDefined(questionsMap.get(parentId)));
  }

  while (parentIdsMap.has(parentId)) {
    parentId = assertDefined(parentIdsMap.get(parentId));
    if (questionsMap.has(parentId)) {
      parents.push(assertDefined(questionsMap.get(parentId)));
    }
  }

  return parents;
}

function shouldQuestionBeHiddenCheck({
  question,
  parentIdsAnswers,
}: {
  question: HRQuestion;
  parentIdsAnswers: HashMap<HRQuestionId, number | null>;
}) {
  if (
    question.parentId === null ||
    !parentIdsAnswers.has(question.parentId) ||
    question.hideOnMobile === true
  ) {
    return question.isHidden;
  }

  return parentIdsAnswers.get(question.parentId) !== Number(question.parentAnswer);
}

export function flattenSectionGroups(section: HRSection) {
  const groups: HRQuestionGroup[] = [];
  const questionGroupIdToSubsectionId = new Map<HRQuestionGroupId, HRSubSectionId>();
  const questionGroupIdShareUrlMap = new Map<HRQuestionGroupId, string | null>();
  const questionGroupIdComplianceMap = new Map<HRQuestionGroupId, boolean | null>();

  for (const subSection of section.subSections) {
    for (const group of subSection.questionGroups) {
      groups.push(group);
      questionGroupIdToSubsectionId.set(group.data.id, subSection.data.id);
      questionGroupIdShareUrlMap.set(group.data.id, subSection.data.shareUrl);
      questionGroupIdComplianceMap.set(group.data.id, subSection.data.isCompliant);
    }
  }

  return {
    groups,
    questionGroupIdToSubsectionId,
    questionGroupIdShareUrlMap,
    questionGroupIdComplianceMap,
  };
}

function getInitialStageIndexForDirections(
  questionGroups: HRQuestionGroup[],
  questionGroupIdToSubsectionId: Map<HRQuestionGroupId, HRSubSectionId>,
  customDirections: CustomOnboardingRedirectionParams
) {
  switch (customDirections.type) {
    case "Question": {
      const customQuestionId = customDirections.questionId;
      const questionGroupIds = [...questionGroupIdToSubsectionId.keys()];
      const index = questionGroups.findIndex(
        (x) =>
          questionGroupIds.includes(x.data.id) &&
          x.questions.find((q) => q.id === customQuestionId) !== undefined
      );
      if (index !== -1) {
        return index;
      }
      break;
    }
    case "Section":
    default:
  }
  // For any case if we dont find the desired question or
  // if we only directed to section
  return getInitialStageIndex(questionGroups);
}

function getInitialStageIndex(questionGroups: HRQuestionGroup[]) {
  const hasAnsweredSomeQuestions = questionGroups.some((group) =>
    group.questions.some(isQuestionAnswered)
  );

  if (hasAnsweredSomeQuestions) {
    const firstGroupIndexWithRejectedAnswers = questionGroups.findIndex(
      (group) =>
        stageHasRejectedDocumentComplianceQuestions(group) || stageHasRejectedQuestionAnswers(group)
    );

    if (firstGroupIndexWithRejectedAnswers !== -1) {
      return firstGroupIndexWithRejectedAnswers;
    }

    const firstGroupIndexWithUnansweredQuestions = questionGroups.findIndex((group) => {
      const hasAnsweredQuestion = group.questions.some(isQuestionAnswered);
      const isReadOnlyQuestionGroup = !group.questions.some(isAnswerableQuestion);

      return (
        !hasAnsweredQuestion &&
        !isReadOnlyQuestionGroup &&
        group.questions.some((question) => !question.isHidden)
      );
    });

    if (firstGroupIndexWithUnansweredQuestions !== -1) {
      return firstGroupIndexWithUnansweredQuestions;
    }
  }

  return 0;
}

function extractTitleFromQuestionGroup(questionGroup: HRQuestionGroup, language: string) {
  const title = getQuestionGroupTitle(questionGroup, language);

  if (title !== "") {
    return title;
  }

  if (questionGroup.questions.every((question) => !isAnswerableQuestion(question))) {
    return "";
  }

  const question = questionGroup.questions.find(
    (q) => getQuestionText(q, language) !== "" && isAnswerableQuestion(q)
  );

  return question === undefined ? "" : getQuestionText(question, language);
}

export default useStage;
